import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import mapboxgl from 'mapbox-gl'
import { MobXProviderContext, observer } from 'mobx-react'
import { MapRef, Marker } from 'react-map-gl/mapbox'

import {
  useResourceActions,
  useResourceMeta,
  useResourceState,
} from '~/client/src/desktop/components/Resource/context'
import MapCanvas from '~/client/src/shared/components/MapBoxViewer/MapCanvas'
import {
  MapControls,
  MapControlsStore,
} from '~/client/src/shared/components/MapControls'
import MapMarker from '~/client/src/shared/components/MapMarker/MapMarker'
import { ResourceCard } from '~/client/src/shared/components/ResourceCardList/ResourceCard'
import ResourceCardList from '~/client/src/shared/components/ResourceCardList/ResourceCardList'
import ResourceCardListGroupHeader from '~/client/src/shared/components/ResourceCardList/ResourceCardListGroupHeader'
import { buildResourceCardModel } from '~/client/src/shared/components/ResourceCardList/resourceCardMapper'
import {
  ICardConfiguration,
  IResourceProperty,
  IResourceRecord,
} from '~/client/src/shared/components/ResourceCardList/types'
import Config from '~/client/src/shared/Config'
import { useResourceCardSchema } from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import {
  getResourceDefinition,
  ResourceSemanticIconKey,
} from '~/client/src/shared/resources/definitions'
import { buildResourceGroupConfig } from '~/client/src/shared/resources/grouping/buildResourceGroupConfig'
import {
  isValidCoordinate,
  useResourceMapMarkers,
} from '~/client/src/shared/resources/hooks/useResourceMapMarkers'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import { resolveResourceValue } from '~/client/src/shared/resources/resolveResourceValue'

const EMPTY_RESOURCE_RECORDS: IResourceRecord[] = []

interface IMapViewport {
  latitude: number
  longitude: number
  zoom: number
}

interface IPopupProps {
  longitude: number
  latitude: number
  model: any
  recordId: string
  leading?: React.ReactNode
  onClose: () => void
  onOpen: (id: string) => void
}

interface IResourceMapLayoutConfig {
  icon: ResourceSemanticIconKey
  countLabel: (count: number) => string
  markerTitleFallback: string
  PopupComponent: React.ComponentType<IPopupProps>
  MarkerComponent?: React.ComponentType<React.ComponentProps<typeof MapMarker>>
  markerAnchor?: React.ComponentProps<typeof Marker>['anchor']
  enrichRecords?: (records: IResourceRecord[]) => IResourceRecord[]
  enrichProperties?: (properties: IResourceProperty[]) => IResourceProperty[]
  enrichCardConfiguration?: (config: ICardConfiguration) => ICardConfiguration
}

const ResourceMapLayout: React.FC<IResourceMapLayoutConfig> = observer(
  props => {
    const { state } = React.useContext(MobXProviderContext) as any
    const center = state?.projectAddress?.center

    if (!center?.lat || !center?.lng) {
      return <div className="tw:flex tw:h-full tw:min-h-0" />
    }

    const viewport: IMapViewport = {
      latitude: center.lat,
      longitude: center.lng,
      zoom: state?.projectAddress?.zoom || 12,
    }

    return <ResourceMapLayoutContent {...props} viewport={viewport} />
  },
)

interface IContentProps extends IResourceMapLayoutConfig {
  viewport: IMapViewport
}

// observer: MapCanvas below reads mapControlsStore.projection and .mapStyle, so without it the
// projection toggle and the basemap switch flip the observable and nothing re-renders.
const ResourceMapLayoutContent: React.FC<IContentProps> = observer(
  ({
    icon,
    countLabel,
    markerTitleFallback,
    PopupComponent,
    MarkerComponent = MapMarker,
    markerAnchor,
    enrichRecords,
    enrichProperties,
    enrichCardConfiguration,
    viewport,
  }) => {
    const { filteredRecords, grouping } = useResourceState()
    const { selectRecord } = useResourceActions()
    const { resourceTypeId, isLoading, fieldProjector, fieldCatalog, schema } =
      useResourceMeta()
    const resourceDefinition = getResourceDefinition(resourceTypeId)
    const latitudeKey = resourceDefinition?.map?.latitudeKey || 'latitude'
    const longitudeKey = resourceDefinition?.map?.longitudeKey || 'longitude'
    const mapRef = useRef<MapRef | null>(null)

    const runtimeContext = useResourceRuntimeContext()
    const {
      cardConfiguration: rawCardConfiguration,
      properties: rawProperties,
      getDisplayValue,
      ready: isSchemaReady,
    } = useResourceCardSchema({
      resourceTypeId,
      runtimeContext,
      schema,
    })

    const records = useMemo(
      () =>
        enrichRecords
          ? enrichRecords(filteredRecords || [])
          : filteredRecords || [],
      [filteredRecords, enrichRecords],
    )

    const fallbackLeadingIcon = useMemo(
      () =>
        renderResourceSemanticIcon(
          icon,
          'tw:block tw:size-full tw:text-muted-foreground',
        ),
      [icon],
    )

    const properties = useMemo(
      () =>
        enrichProperties ? enrichProperties(rawProperties) : rawProperties,
      [rawProperties, enrichProperties],
    )

    const cardConfiguration = useMemo(
      () =>
        enrichCardConfiguration
          ? enrichCardConfiguration(rawCardConfiguration)
          : rawCardConfiguration,
      [rawCardConfiguration, enrichCardConfiguration],
    )

    const groupConfig = useMemo(() => {
      if (!grouping) return undefined

      return buildResourceGroupConfig({
        fieldProjector,
        grouping,
        hierarchy: fieldCatalog.getField(grouping)?.hierarchy,
      })
    }, [fieldProjector, fieldCatalog, grouping])

    const loading = !isSchemaReady || isLoading

    const mapMarkers = useResourceMapMarkers(records, resourceTypeId)

    const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(
      null,
    )

    const selectedPopup = useMemo(() => {
      if (!selectedMarkerId) return null

      const marker = mapMarkers.find(m => m.id === selectedMarkerId)
      if (!marker) return null

      const record = records.find(r => r.id === selectedMarkerId)
      if (!record) return null

      const model = buildResourceCardModel({
        record,
        resourceTypeId,
        cardConfiguration,
        properties,
        getDisplayValue,
      })

      return { marker, model, record }
    }, [
      selectedMarkerId,
      mapMarkers,
      records,
      resourceTypeId,
      cardConfiguration,
      properties,
      getDisplayValue,
    ])

    const handleClosePopup = useCallback(() => {
      setSelectedMarkerId(null)
    }, [])

    const { latitude, longitude, zoom } = viewport
    const mapboxAccessToken = mapboxgl.accessToken || Config.MAPBOX_TOKEN

    const [mapControlsStore] = useState(() => new MapControlsStore(viewport))

    useEffect(() => () => mapControlsStore.dispose(), [mapControlsStore])

    const mapPaneRef = useRef<HTMLDivElement | null>(null)

    const handleMapRef = useCallback(
      (ref: MapRef | null) => {
        mapRef.current = ref
        mapControlsStore.setMapRef(ref)
      },
      [mapControlsStore],
    )

    useEffect(() => {
      const container = mapPaneRef.current
      if (!container) return

      let lastW = 0
      let lastH = 0
      const observer = new ResizeObserver(entries => {
        const entry = entries[0]
        if (!entry) return
        const { width, height } = entry.contentRect
        if (width === lastW && height === lastH) return
        lastW = width
        lastH = height
        mapRef.current?.resize()
      })
      observer.observe(container)
      return () => observer.disconnect()
    }, [])

    const handleCardClick = useCallback(
      (record: IResourceRecord) => {
        const markerLatitude = Number(
          resolveResourceValue({
            record,
            resourceTypeId,
            key: latitudeKey,
          }),
        )
        const markerLongitude = Number(
          resolveResourceValue({
            record,
            resourceTypeId,
            key: longitudeKey,
          }),
        )

        selectRecord(record.id)

        if (!isValidCoordinate(markerLatitude, markerLongitude)) {
          return
        }

        setSelectedMarkerId(record.id)
        mapRef.current?.getMap()?.flyTo({
          center: [markerLongitude, markerLatitude],
          duration: 800,
          essential: true,
        })
      },
      [resourceTypeId, latitudeKey, longitudeKey, selectRecord],
    )

    return (
      <div className="tw:flex tw:h-full tw:min-h-0">
        <div className="tw:flex tw:flex-col tw:basis-[360px] tw:grow-0 tw:shrink-0 tw:min-w-[320px] tw:border-0 tw:border-r tw:border-solid tw:border-border tw:h-full tw:min-h-0 tw:[&_.resource-card-list]:h-full tw:[&_.card-list]:h-full">
          <ResourceCardList
            resourceTypeId={resourceTypeId}
            records={isSchemaReady ? records : EMPTY_RESOURCE_RECORDS}
            cardConfiguration={cardConfiguration}
            properties={properties}
            getDisplayValue={getDisplayValue}
            loading={loading}
            groupConfig={groupConfig}
            countLabel={countLabel}
            renderGroupHeader={(group, toggle) => (
              <ResourceCardListGroupHeader
                group={group}
                onToggle={toggle}
                grouping={grouping}
                properties={properties}
                resourceTypeId={resourceTypeId}
              />
            )}
          >
            {(record, model) => (
              <ResourceCard.Standard
                model={model}
                variant="menu"
                // The record open on the map reads as the selected row.
                className={
                  record.id === selectedMarkerId
                    ? 'tw:bg-accent-soft'
                    : 'tw:transition-colors tw:hover:bg-accent-soft'
                }
                onClick={() => handleCardClick(record)}
                leading={renderResourceRecordIcon(
                  resourceTypeId,
                  record,
                  fallbackLeadingIcon,
                  'tw:block tw:size-full tw:text-muted-foreground',
                )}
              />
            )}
          </ResourceCardList>
        </div>
        <div
          ref={mapPaneRef}
          className="tw:flex tw:flex-col tw:grow tw:bg-background tw:h-full tw:min-h-0"
        >
          <MapControls.Provider store={mapControlsStore}>
            <MapCanvas
              ref={handleMapRef}
              initialViewState={{ longitude, latitude, zoom }}
              style={{ width: '100%', height: '100%' }}
              mapStyle={mapControlsStore.mapStyle}
              mapboxAccessToken={mapboxAccessToken}
              projection={mapControlsStore.projection}
              isTrafficEnabled={
                mapControlsStore.globeViewStyle.isTrafficEnabled
              }
              isTerrainEnabled={
                mapControlsStore.globeViewStyle.isTerrainEnabled
              }
              isThreeDEnabled={mapControlsStore.globeViewStyle.isThreeDEnabled}
              shouldHideLabels={
                mapControlsStore.globeViewStyle.shouldHideLabels
              }
            >
              {mapMarkers.map(marker => (
                <Marker
                  key={marker.id}
                  longitude={marker.longitude}
                  latitude={marker.latitude}
                  anchor={markerAnchor}
                >
                  <MarkerComponent
                    isSelected={marker.id === selectedMarkerId}
                    title={marker.name || markerTitleFallback}
                    onClick={() => setSelectedMarkerId(marker.id)}
                  />
                </Marker>
              ))}
              {selectedPopup && (
                <PopupComponent
                  longitude={selectedPopup.marker.longitude}
                  latitude={selectedPopup.marker.latitude}
                  model={selectedPopup.model}
                  recordId={selectedPopup.marker.id}
                  leading={renderResourceRecordIcon(
                    resourceTypeId,
                    selectedPopup.record,
                    fallbackLeadingIcon,
                    'tw:block tw:size-full tw:text-muted-foreground',
                  )}
                  onClose={handleClosePopup}
                  onOpen={selectRecord}
                />
              )}
            </MapCanvas>
            <MapControls.Chrome />
          </MapControls.Provider>
        </div>
      </div>
    )
  },
)

export default ResourceMapLayout
